import api from './api';
import moment, { Moment } from 'moment';

/** API overview response (snake_case). Validate and map to DashboardKPIs. */
interface OverviewResponseData {
  total_revenue?: number;
  total_orders?: number;
  active_merchants?: number;
  active_users?: number;
  from?: string;
  to?: string;
}

function ensureNumber(x: unknown): number {
  const n = typeof x === 'number' ? x : Number(x);
  return Number.isFinite(n) ? n : 0;
}

/** Runtime validation: normalize overview payload to avoid undefined => 0 silent bugs. */
function parseOverviewData(raw: unknown): {
  totalRevenue: number;
  totalOrders: number;
  activeMerchants: number;
  activeUsers: number;
} {
  const data =
    raw && typeof raw === 'object' && 'data' in raw
      ? (raw as { data: OverviewResponseData }).data
      : (raw as OverviewResponseData);
  if (!data || typeof data !== 'object') {
    return {
      totalRevenue: 0,
      totalOrders: 0,
      activeMerchants: 0,
      activeUsers: 0,
    };
  }
  const d = data as Record<string, unknown>;
  return {
    totalRevenue: ensureNumber(d.total_revenue ?? d.totalRevenue),
    totalOrders: ensureNumber(d.total_orders ?? d.totalOrders),
    activeMerchants: ensureNumber(d.active_merchants ?? d.activeMerchants),
    activeUsers: ensureNumber(d.active_users ?? d.activeUsers),
  };
}

export interface DateRange {
  startDate: string;
  endDate: string;
}

export interface DashboardKPIs {
  totalRevenue: number;
  totalOrders: number;
  activeMerchants: number;
  newUsers: number;
  // Previous period for comparison
  previousRevenue?: number;
  previousOrders?: number;
  previousActiveMerchants?: number;
  previousNewUsers?: number;
}

export interface RevenueTrendData {
  date: string;
  revenue: number;
  orders: number;
}

export interface OrderStatusBreakdown {
  pending: number;
  confirmed: number;
  preparing: number;
  ready: number;
  delivering: number;
  delivered: number;
  cancelled: number;
}

export interface DailyOrderStatus {
  date: string;
  pending: number;
  confirmed: number;
  preparing: number;
  ready: number;
  delivering: number;
  delivered: number;
  cancelled: number;
  total: number;
}

export interface TopMerchant {
  id: number;
  name: string;
  revenue: number;
  orders: number;
}

export interface RecentOrder {
  id: number;
  order_number: string;
  customer_name?: string;
  customerName?: string;
  merchant_name?: string;
  restaurant_name?: string;
  total: number;
  total_amount?: number;
  status: string;
  created_at: string;
  payment_method?: string;
}

export interface PaymentMethodBreakdown {
  [key: string]: number; // e.g. { COD: 50, MOMO: 30, BANK: 20 }
}

export interface DashboardData {
  kpis: DashboardKPIs;
  revenueTrend: RevenueTrendData[];
  orderStatusBreakdown: OrderStatusBreakdown;
  dailyOrderStatus: DailyOrderStatus[]; // For stacked bar chart
  topMerchants: TopMerchant[];
  recentOrders: RecentOrder[];
  paymentMethodBreakdown: PaymentMethodBreakdown;
  cancellationRate?: number;
  avgTimeToConfirm?: number;
  avgTimeToDeliver?: number;
}

export type DatePeriod = 'today' | '7d' | '30d' | 'custom';

/**
 * Calculate date range based on period selection
 */
export const getDateRange = (
  period: DatePeriod,
  customRange?: [Moment, Moment] | null
): DateRange => {
  const now = new Date();
  const endDate = new Date(now);
  endDate.setHours(23, 59, 59, 999);

  let startDate: Date;

  switch (period) {
    case 'today':
      startDate = new Date(now);
      startDate.setHours(0, 0, 0, 0);
      break;
    case '7d':
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 7);
      startDate.setHours(0, 0, 0, 0);
      break;
    case '30d':
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 30);
      startDate.setHours(0, 0, 0, 0);
      break;
    case 'custom':
      if (customRange && customRange[0] && customRange[1]) {
        startDate = customRange[0].toDate();
        endDate.setTime(customRange[1].toDate().getTime());
        endDate.setHours(23, 59, 59, 999);
      } else {
        // Fallback to 7d if custom range not provided
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 7);
        startDate.setHours(0, 0, 0, 0);
      }
      break;
    default:
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 7);
      startDate.setHours(0, 0, 0, 0);
  }

  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  };
};

/**
 * Get previous period date range for comparison
 */
export const getPreviousPeriodRange = (dateRange: DateRange): DateRange => {
  const start = new Date(dateRange.startDate);
  const end = new Date(dateRange.endDate);
  const diffDays = Math.ceil(
    (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
  );

  const prevEnd = new Date(start);
  prevEnd.setDate(prevEnd.getDate() - 1);
  prevEnd.setHours(23, 59, 59, 999);

  const prevStart = new Date(prevEnd);
  prevStart.setDate(prevStart.getDate() - diffDays);
  prevStart.setHours(0, 0, 0, 0);

  return {
    startDate: prevStart.toISOString().split('T')[0],
    endDate: prevEnd.toISOString().split('T')[0],
  };
};

/**
 * Fetch dashboard data: KPIs from /dashboard/overview (same timeRange), charts from orders.
 * On request failure we throw so UI shows error state (no silent fallback to 0).
 */
export const fetchDashboardData = async (
  dateRange: DateRange
): Promise<DashboardData> => {
  try {
    const overviewParams = {
      from: dateRange.startDate,
      to: dateRange.endDate,
    };

    let overviewRes: { data: unknown };
    try {
      overviewRes = await api.get('/dashboard/overview', {
        params: overviewParams,
      });
    } catch (err) {
      throw err;
    }

    const current = parseOverviewData(overviewRes?.data);

    const previousRange = getPreviousPeriodRange(dateRange);
    const prevParams = {
      from: previousRange.startDate,
      to: previousRange.endDate,
    };
    let previous = {
      totalRevenue: 0,
      totalOrders: 0,
      activeMerchants: 0,
      activeUsers: 0,
    };
    try {
      const prevRes = await api.get('/dashboard/overview', {
        params: prevParams,
      });
      previous = parseOverviewData(prevRes?.data);
    } catch {
      // Previous period optional — keep zeros
    }

    let ordersRes: { data?: { data?: unknown[] } };
    try {
      ordersRes = await api.get('/orders', {
        params: {
          from: dateRange.startDate,
          to: dateRange.endDate,
          limit: 2000,
          page: 1,
        },
      });
    } catch (err) {
      throw err;
    }

    const ordersRaw = ordersRes?.data?.data;
    let orders: any[] = Array.isArray(ordersRaw) ? ordersRaw : [];

    const startDateFilter = new Date(dateRange.startDate);
    const endDateFilter = new Date(dateRange.endDate);
    endDateFilter.setHours(23, 59, 59, 999);

    orders = orders.filter((order: any) => {
      const orderDate = new Date(order.created_at || order.createdAt);
      return orderDate >= startDateFilter && orderDate <= endDateFilter;
    });

    const getOrderTotal = (order: any): number => {
      const v = order.total ?? order.total_amount ?? order.totalAmount ?? 0;
      return Number(v) || 0;
    };

    /** GMV: đơn chưa hủy (khớp Founder Dashboard / dashboardFounderService). */
    const countsTowardGmv = (order: any): boolean => {
      const status = String(order.status ?? '').toLowerCase();
      return status !== 'cancelled';
    };

    const kpis: DashboardKPIs = {
      totalRevenue: current.totalRevenue,
      totalOrders: current.totalOrders,
      activeMerchants: current.activeMerchants,
      newUsers: current.activeUsers,
      previousRevenue: previous.totalRevenue,
      previousOrders: previous.totalOrders,
      // Active Merchants & Active Users are platform-wide counts (not date-filtered),
      // so no previous period comparison
      previousActiveMerchants: undefined,
      previousNewUsers: undefined,
    };

    // Calculate order status breakdown (overall)
    const orderStatusBreakdown: OrderStatusBreakdown = {
      pending: 0,
      confirmed: 0,
      preparing: 0,
      ready: 0,
      delivering: 0,
      delivered: 0,
      cancelled: 0,
    };

    // Calculate daily order status breakdown (for stacked bar chart)
    const dailyStatusMap = new Map<string, OrderStatusBreakdown>();

    orders.forEach((order: any) => {
      const status = order.status?.toLowerCase() || 'pending';
      if (orderStatusBreakdown.hasOwnProperty(status)) {
        orderStatusBreakdown[status as keyof OrderStatusBreakdown]++;
      }

      // Group by date for daily breakdown
      const orderDate = new Date(order.created_at || order.createdAt)
        .toISOString()
        .split('T')[0];

      if (!dailyStatusMap.has(orderDate)) {
        dailyStatusMap.set(orderDate, {
          pending: 0,
          confirmed: 0,
          preparing: 0,
          ready: 0,
          delivering: 0,
          delivered: 0,
          cancelled: 0,
        });
      }

      const dailyStatus = dailyStatusMap.get(orderDate)!;
      if (dailyStatus.hasOwnProperty(status)) {
        dailyStatus[status as keyof OrderStatusBreakdown]++;
      }
    });

    // Convert daily status map to array
    const dailyOrderStatus: DailyOrderStatus[] = Array.from(
      dailyStatusMap.entries()
    )
      .map(([date, status]) => ({
        date,
        ...status,
        total: Object.values(status).reduce((sum, count) => sum + count, 0),
      }))
      .sort((a, b) => a.date.localeCompare(b.date));

    // Get recent orders (last 10)
    const recentOrders: RecentOrder[] = orders
      .slice(0, 12)
      .map((order: any) => ({
        id: order.id,
        order_number: order.order_number || order.orderNumber,
        customer_name: order.customer_name || order.customerName,
        customerName: order.customer_name || order.customerName,
        merchant_name:
          order.restaurant_name || order.merchant_name || order.restaurantName,
        restaurant_name: order.restaurant_name || order.restaurantName,
        total: getOrderTotal(order),
        total_amount: getOrderTotal(order),
        status: order.status,
        created_at: order.created_at || order.createdAt,
        payment_method: order.payment_method || order.paymentMethod,
      }));

    // Calculate revenue trend (group by day)
    const revenueByDate = new Map<
      string,
      { revenue: number; orders: number }
    >();

    orders.forEach((order: any) => {
      const date = new Date(order.created_at || order.createdAt)
        .toISOString()
        .split('T')[0];
      const existing = revenueByDate.get(date) || { revenue: 0, orders: 0 };
      revenueByDate.set(date, {
        revenue:
          existing.revenue +
          (countsTowardGmv(order) ? getOrderTotal(order) : 0),
        orders: existing.orders + 1,
      });
    });

    const revenueTrend: RevenueTrendData[] = Array.from(revenueByDate.entries())
      .map(([date, data]) => ({
        date,
        revenue: data.revenue,
        orders: data.orders,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));

    // Calculate top merchants by restaurant_id (from orders)
    const merchantRevenue = new Map<
      number,
      { name: string; revenue: number; orders: number }
    >();

    orders.forEach((order: any) => {
      const restaurantId =
        order.restaurant_id || order.merchantId || order.restaurantId;
      if (!restaurantId) return;
      const merchantName =
        order.restaurant_name ||
        order.merchant_name ||
        order.restaurantName ||
        'Unknown';
      const existing = merchantRevenue.get(restaurantId);
      const gmvDelta = countsTowardGmv(order) ? getOrderTotal(order) : 0;
      if (existing) {
        merchantRevenue.set(restaurantId, {
          name: existing.name,
          revenue: existing.revenue + gmvDelta,
          orders: existing.orders + 1,
        });
      } else {
        merchantRevenue.set(restaurantId, {
          name: merchantName,
          revenue: gmvDelta,
          orders: 1,
        });
      }
    });

    // Resolve restaurant_id -> user_id for /merchants/:id route (MerchantList uses user id)
    const restaurantToUserId = new Map<number, number>();
    try {
      const merchantsRes = await api.get<{
        success: boolean;
        data?: Array<{ id: number; restaurantId?: number }>;
      }>('/merchants', { params: { limit: 500, page: 1 } });
      const merchants = merchantsRes?.data?.data ?? [];
      merchants.forEach((m: { id: number; restaurantId?: number }) => {
        const rid =
          m.restaurantId ?? (m as Record<string, number>).restaurant_id;
        if (rid != null) restaurantToUserId.set(Number(rid), m.id);
      });
    } catch {
      // Top merchants still work with restaurant ids as fallback
    }

    const topMerchants: TopMerchant[] = Array.from(merchantRevenue.entries())
      .map(([restaurantId, data]) => ({
        id: restaurantToUserId.get(restaurantId) ?? restaurantId,
        name: data.name,
        revenue: data.revenue,
        orders: data.orders,
      }))
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10);

    // Calculate cancellation rate
    const totalOrdersCount = orders.length;
    const cancelledCount = orderStatusBreakdown.cancelled;
    const cancellationRate =
      totalOrdersCount > 0 ? (cancelledCount / totalOrdersCount) * 100 : 0;

    // Payment method breakdown
    const paymentMethodBreakdown: PaymentMethodBreakdown = {};
    orders.forEach((order: any) => {
      const pm = (order.payment_method || order.paymentMethod || 'COD')
        .toString()
        .toUpperCase();
      const key = pm === 'CASH' ? 'COD' : pm || 'OTHER';
      paymentMethodBreakdown[key] = (paymentMethodBreakdown[key] || 0) + 1;
    });

    return {
      kpis,
      revenueTrend,
      orderStatusBreakdown,
      dailyOrderStatus,
      topMerchants,
      recentOrders,
      paymentMethodBreakdown,
      cancellationRate,
    };
  } catch (error) {
    throw error;
  }
};
